/**
 * The Stats panel in the footer: lifetime totals that the top-of-shop readout
 * doesn't show. Updated from the render loop like everything else; each cell
 * writes to the DOM only when its text changes.
 */

import { ACHIEVEMENTS } from '../data/achievements';
import { availableDinos } from '../data/dinos';
import { FOSSIL_BONUS } from '../data/eras';

import { UPGRADES } from '../data/upgrades';
import { offlineRate } from '../game/economy';
import { eraModifier, expedition } from '../game/rebirth';
import { formatEggs, formatRate } from '../game/numbers';
import type { GameState } from '../game/state';

export interface LifetimeStatsView {
  readonly body: HTMLElement;
  update(state: GameState, eps: number, perClick: number): void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function createLifetimeStatsView(): LifetimeStatsView {
  const body = el('div', 'settings__body stats');
  const list = el('dl', 'stats__list');
  body.append(list);

  /** Adds a row and returns a setter that writes only on change. */
  function row(label: string): (text: string) => void {
    const dt = el('dt', 'stats__label', label);
    const dd = el('dd', 'stats__value', '');
    list.append(dt, dd);
    let last: string | null = null;
    return (text) => {
      if (text === last) return;
      last = text;
      dd.textContent = text;
    };
  }

  const eggs = row('Eggs in hand');
  const thisEra = row('Eggs laid, this era');
  const lifetime = row('Eggs laid, all time');
  const rate = row('Eggs per second');
  const click = row('Eggs per click');
  const offline = row('Away rate');
  const clicks = row('Egg clicks');
  const levels = row('Dinosaur levels');
  const tiers = row('Species hatched');
  const upgrades = row('Upgrades bought');
  const achievements = row('Achievements');
  const cracks = row('Golden eggs caught');
  const meteors = row('Meteors broken');
  const shards = row('Meteor shards earned');
  const era = row('Era');
  const fossils = row('Fossils held');
  const fossilsEarned = row('Fossils earned');

  // The Era Log used to be here, one run-on line each. It is the Museum's
  // History drawer now: a museum is the right place for what has been, and a
  // plaque has room for the dinosaur the era is remembered by.

  return {
    body,
    update(state, epsValue, perClick) {
      eggs(formatEggs(state.eggs));
      thisEra(formatEggs(state.eggsThisEra));
      lifetime(formatEggs(state.totalEggs));
      rate(formatRate(epsValue));
      click(formatRate(perClick));
      offline(`${Math.round(offlineRate(state) * 100)}% of eggs per second`);
      clicks(formatEggs(state.clicks));
      let total = 0;
      let species = 0;
      const offered = availableDinos(state.eraId);
      for (const def of offered) {
        const n = state.dinos[def.id] ?? 0;
        total += n;
        if (n > 0) species++;
      }
      levels(formatEggs(total));
      tiers(`${species} / ${offered.length}`);
      upgrades(`${state.upgrades.length} / ${UPGRADES.length}`);
      achievements(`${state.achievements.length} / ${ACHIEVEMENTS.length}`);
      cracks(formatEggs(state.cracks));
      meteors(formatEggs(state.meteorsBroken));
      shards(formatEggs(state.shardsEarned));
      // Eras count from 1 for the player: state.era is how many have *ended*.
      // The modifier and expedition ride along, since the number alone does
      // not say which era you are in.
      era(
        [`${state.era + 1}`, eraModifier(state)?.name, expedition(state)?.name]
          .filter(Boolean)
          .join(' · '),
      );
      fossils(
        `${formatEggs(state.fossils)} (+${Math.round(state.fossils * FOSSIL_BONUS * 100)}% production)`,
      );
      fossilsEarned(formatEggs(state.fossilsEarned));
    },
  };
}
